import { useState } from 'react';
import { useForm } from '@refinedev/react-hook-form';
import type { HttpError } from '@refinedev/core';
import { useNavigate } from 'react-router';
import type { TfTrackRow, TrackManifest } from '@verybigthings/trackframe-shared/schemas';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

// HUB-062 / DEV-014: registration is a manifest upsert. The admin form is a
// best-effort surface for ad-hoc operator registration; the canonical track
// onboarding flow is the track-side `registerTrack()` helper, which pushes
// the resolved trackframe.config.json.
interface FormValues {
  name: string;
  displayName: string;
  description: string;
  apiUrl: string;
  baseUrl: string;
  healthCheckUrl: string;
  openapiJson: string;
  systemPrompt: string;
}

interface ManifestDraft extends Partial<TrackManifest> {
  openapi?: Record<string, unknown>;
}

type ParseResult =
  | { ok: true; value: Record<string, unknown> }
  | { ok: false; error: string };

function parseOpenapi(text: string): ParseResult {
  if (!text.trim()) {
    return {
      ok: true,
      value: { openapi: '3.0.0', info: { title: 'track', version: '1' }, paths: {} },
    };
  }
  try {
    const parsed = JSON.parse(text);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return { ok: false, error: 'OpenAPI must be a JSON object' };
    }
    return { ok: true, value: parsed as Record<string, unknown> };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'Invalid JSON' };
  }
}

function toBody(v: FormValues): ManifestDraft | { error: string } {
  const parsed = parseOpenapi(v.openapiJson);
  if (!parsed.ok) return { error: parsed.error };
  return {
    name: v.name.trim(),
    displayName: v.displayName.trim() || v.name.trim(),
    ...(v.description.trim() ? { description: v.description.trim() } : {}),
    apiUrl: v.apiUrl.trim(),
    ...(v.baseUrl.trim() ? { baseUrl: v.baseUrl.trim() } : {}),
    ...(v.healthCheckUrl.trim() ? { healthCheckUrl: v.healthCheckUrl.trim() } : {}),
    openapi: parsed.value,
    ...(v.systemPrompt.trim() ? { systemPrompt: v.systemPrompt.trim() } : {}),
  };
}

export function TracksCreate() {
  const navigate = useNavigate();
  const [parseError, setParseError] = useState<string | null>(null);
  const {
    refineCore: { onFinish },
    register,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<TfTrackRow, HttpError, FormValues>({
    refineCoreProps: { onMutationSuccess: () => navigate('/admin/tracks') },
  });

  const submit = handleSubmit(async (values) => {
    const body = toBody(values);
    if ('error' in body) {
      setParseError(body.error);
      return;
    }
    setParseError(null);
    await onFinish(body as unknown as FormValues);
  });

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold tracking-tight">Register track</h1>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Track manifest</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="mb-4 text-sm text-muted-foreground">
            The preferred onboarding flow is calling <code className="font-mono">registerTrack()</code> from the
            track's hub-client. This form is a manual fallback — the operator owns the manifest fields.
          </p>
          <form className="space-y-4" onSubmit={submit}>
            <div className="space-y-2">
              <Label htmlFor="name">Name (slug)</Label>
              <Input id="name" required placeholder="agreements" {...register('name')} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="displayName">Display name</Label>
              <Input id="displayName" placeholder="Agreements" {...register('displayName')} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input id="description" {...register('description')} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="apiUrl">API URL</Label>
              <Input id="apiUrl" type="url" required placeholder="https://api.example.com" {...register('apiUrl')} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="baseUrl">Frontend URL</Label>
              <Input id="baseUrl" type="url" placeholder="https://app.example.com" {...register('baseUrl')} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="healthCheckUrl">Health-check URL</Label>
              <Input id="healthCheckUrl" type="url" placeholder="https://api.example.com/health" {...register('healthCheckUrl')} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="openapiJson">OpenAPI document (JSON)</Label>
              <textarea
                id="openapiJson"
                className="w-full min-h-[140px] rounded-md border bg-background px-3 py-2 font-mono text-xs"
                placeholder='Paste your OpenAPI 3.x document — leave blank for an empty starter spec.'
                {...register('openapiJson')}
              />
              {parseError ? <p className="text-xs text-destructive">{parseError}</p> : null}
            </div>
            <div className="space-y-2">
              <Label htmlFor="systemPrompt">System prompt</Label>
              <textarea
                id="systemPrompt"
                className="w-full min-h-[80px] rounded-md border bg-background px-3 py-2 text-sm"
                {...register('systemPrompt')}
              />
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Creating…' : 'Create'}
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate('/admin/tracks')}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
